import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Users, MapPin, Mail, Phone, FileText, Clock, CheckCircle, MessageSquare, Image as ImageIcon, Eye, Hash, Briefcase, Calendar, User, Info } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { formatDate } from '../../../utils/stringUtils';
import fieldSalesService from '../../../api/crm/field-sales/fieldSalesService';

const RoutePlanDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data: routePlan, isLoading, error } = useQuery({
        queryKey: ['fieldSales', 'routePlan', id],
        queryFn: async () => {
            const response = await fieldSalesService.getRoutePlanById(id);
            if (response.success) {
                return response.data;
            }
            throw new Error(response.error || 'Failed to fetch route plan details');
        },
        enabled: !!id
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                <span className="ml-3 text-gray-500">Loading route plan details...</span>
            </div>
        );
    }

    if (error || !routePlan) {
        return (
            <div className="flex flex-col items-center justify-center p-12 space-y-4">
                <div className="text-red-500 text-lg">Failed to load route plan details</div>
                <Button variant="outline" onClick={() => navigate(-1)} icon={<ArrowLeft className="h-4 w-4" />}>
                    Go Back
                </Button>
            </div>
        );
    }

    // Format time from 24-hour (HH:MM) to 12-hour (HH:MM AM/PM) format
    const formatTimeTo12Hour = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const timeParts = timeString.split(':');
            if (timeParts.length < 2) return timeString;
            const hours = parseInt(timeParts[0], 10);
            const minutes = timeParts[1];
            if (isNaN(hours) || hours < 0 || hours > 23) return timeString;
            const period = hours >= 12 ? 'PM' : 'AM';
            const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            return `${displayHours}:${minutes} ${period}`;
        } catch (error) {
            return timeString;
        }
    };

    // Format status to human-readable format
    const formatStatus = (status) => {
        if (!status) return 'Scheduled';
        return status
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Get status color classes
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'text-green-600 bg-green-50';
            case 'in-progress':
            case 'in-process': return 'text-[#0F2942] bg-blue-50';
            case 'scheduled': return 'text-gray-600 bg-gray-50';
            case 'cancelled': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    // Calculate visit duration from start and end times
    const calculateVisitDuration = (start, end) => {
        if (!start || !end) return null;
        try {
            const startTime = new Date(start);
            const endTime = new Date(end);
            const diffMs = endTime - startTime;
            if (diffMs < 0) return '0 seconds';
            const totalSeconds = Math.floor(diffMs / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            let durationParts = [];
            if (hours > 0) durationParts.push(`${hours} hours`);
            if (minutes > 0) durationParts.push(`${minutes} minutes`);
            if (hours === 0 && minutes === 0) return `${seconds} seconds`;
            if (durationParts.length === 0) return '0 seconds';
            return durationParts.join(' ');
        } catch (error) {
            return null;
        }
    };

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="flex items-center space-x-4">
                    <Button variant="ghost" className="p-2 border" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-5 w-5 text-gray-600" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Route Plan Details</h1>
                        <p className="text-gray-600 text-sm mt-1">View complete route plan information</p>
                    </div>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-8">
                {/* Top Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center space-x-4">
                        <div className="p-3 bg-green-50 rounded-lg">
                            <FileText className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Route Plan ID</p>
                            <p className="text-lg font-bold text-gray-900">{routePlan.routePlanId || routePlan.id || routePlan._id || 'N/A'}</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center space-x-4">
                        <div className="p-3 bg-orange-50 rounded-lg">
                            <Calendar className="h-6 w-6 text-orange-600" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Visit Date</p>
                            <div className="text-lg font-bold text-gray-900 leading-tight">
                                {routePlan.date ? (
                                    <>
                                        <div>{formatDate(routePlan.date, { includeTime: false })}</div>
                                        {routePlan.customers?.[0]?.time && (
                                            <div className="text-xs font-bold text-gray-400 mt-1 flex items-center">
                                                <Clock className="h-3 w-3 mr-1" />
                                                Starts at: {formatTimeTo12Hour(routePlan.customers[0].time)}
                                            </div>
                                        )}
                                    </>
                                ) : 'N/A'}
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center space-x-4">
                        <div className="p-3 bg-purple-50 rounded-lg">
                            <Clock className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Estimated Time</p>
                            <p className="text-lg font-bold text-gray-900">
                                {routePlan.estimatedTime
                                    ? `${Math.floor(routePlan.estimatedTime / 60)}h ${routePlan.estimatedTime % 60}m`
                                    : 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Assignee Details Section */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center space-x-2">
                        <User className="h-5 w-5 text-[#0F2942]" />
                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Assignee Information</h3>
                    </div>
                    <div className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center gap-8">
                            {/* Avatar Circle / Profile Picture */}
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center border-4 border-blue-50 overflow-hidden flex-shrink-0">
                                {routePlan.rep?.profilePicture ? (
                                    <img
                                        src={routePlan.rep.profilePicture}
                                        alt={routePlan.rep?.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-2xl font-bold text-[#0F2942] uppercase">
                                        {(routePlan.rep?.name || 'U').charAt(0)}
                                    </span>
                                )}
                            </div>

                            {/* Rep Info Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 flex-1">
                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Name</label>
                                    <p className="text-base font-bold text-gray-900 capitalize break-words">
                                        {routePlan.rep?.name || (typeof routePlan.rep === 'string' ? routePlan.rep : 'N/A')}
                                    </p>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Employee ID</label>
                                    <div className="flex items-center space-x-2 text-gray-700">
                                        <Hash className="h-4 w-4 text-gray-400" />
                                        <p className="text-base font-semibold">{routePlan.rep?.employeeId || 'N/A'}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Department</label>
                                    <div className="flex items-start space-x-2 text-gray-700">
                                        <div className="mt-1 flex-shrink-0">
                                            <Briefcase className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <p className="text-base font-semibold capitalize break-all break-words">{routePlan.rep?.department || 'N/A'}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Email Address</label>
                                    <div className="flex items-start space-x-2">
                                        <div className="mt-1 flex-shrink-0">
                                            <Mail className="h-4 w-4 text-blue-500" />
                                        </div>
                                        <p
                                            className="text-base font-semibold break-all"
                                        >
                                            {routePlan.repEmail || routePlan.rep?.email || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customers List */}
                <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Users className="h-5 w-5 mr-2 text-[#0F2942]" />
                        Customer List
                    </h4>
                    <div className="space-y-4">
                        {routePlan.customers && routePlan.customers.length > 0 ? (
                            routePlan.customers.map((customer, index) => (
                                <div key={customer.id || customer._id || `customer_${index}`} className="border border-gray-200 rounded-lg p-5 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-4 flex-1 min-w-0">
                                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <span className="text-sm font-bold text-[#0F2942]">{index + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h5 className="text-lg font-semibold text-gray-900 mb-3 break-words capitalize">{customer.name || 'N/A'}</h5>
                                                <div className="space-y-3">
                                                    {/* Email and Phone */}
                                                    {(customer.email || customer.phone) && (
                                                        <div className="flex flex-wrap items-center gap-4">
                                                            {customer.email && (
                                                                <div className="flex items-center min-w-0">
                                                                    <Mail className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                                                                    <a
                                                                        href={`mailto:${customer.email}`}
                                                                        className="text-sm text-[#0F2942] hover:text-blue-800 hover:underline break-all"
                                                                    >
                                                                        {customer.email}
                                                                    </a>
                                                                </div>
                                                            )}
                                                            {customer.phone && (
                                                                <div className="flex items-center min-w-0">
                                                                    <Phone className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                                                                    <a
                                                                        href={`tel:${customer.phone}`}
                                                                        className="text-sm text-[#0F2942] hover:text-blue-800 hover:underline"
                                                                    >
                                                                        {customer.phone}
                                                                    </a>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Address */}
                                                    <div className="flex items-start min-w-0">
                                                        <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                                                        <p
                                                            className="text-sm text-[#0F2942] break-words capitalize flex-1 min-w-0 cursor-pointer hover:text-blue-800 hover:underline"
                                                            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address || '')}`, '_blank')}
                                                        >
                                                            {customer.address || 'N/A'}
                                                        </p>
                                                    </div>

                                                    {/* Visit Details */}
                                                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${customer.visitStart && customer.visitEnd ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 pt-3 border-t border-gray-100`}>
                                                        <div>
                                                            <label className="text-xs font-semibold text-gray-500 uppercase">Scheduled Time</label>
                                                            <p className="text-sm font-medium text-gray-900 mt-1">{formatTimeTo12Hour(customer.time)}</p>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs font-semibold text-gray-500 uppercase">Planned Duration</label>
                                                            <p className="text-sm font-medium text-gray-900 mt-1">{customer.duration ? `${customer.duration} minutes` : 'N/A'}</p>
                                                        </div>
                                                        {customer.visitStart && customer.visitEnd && (
                                                            <div>
                                                                <label className="text-xs font-semibold text-gray-500 uppercase">Actual Duration</label>
                                                                <p className="text-sm font-bold text-[#0F2942] mt-1">
                                                                    {calculateVisitDuration(customer.visitStart.timestamp, customer.visitEnd.timestamp)}
                                                                </p>
                                                            </div>
                                                        )}
                                                        <div>
                                                            <label className="text-xs font-semibold text-gray-500 uppercase">Status</label>
                                                            <div className="mt-1">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(customer.status || 'scheduled')}`}>
                                                                    {formatStatus(customer.status || 'scheduled')}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Field Visit Execution Details */}
                                                    {(customer.visitStart || customer.visitEnd || customer.visitOutcome || customer.visitNotes || (customer.visitImages && customer.visitImages.length > 0)) && (
                                                        <div className="mt-4 pt-4 border-t border-dashed border-gray-200 bg-blue-50/30 p-4 rounded-lg">
                                                            <h6 className="text-sm font-bold text-gray-800 mb-4 flex items-center">
                                                                <CheckCircle className="h-4 w-4 mr-1.5 text-[#0F2942]" />
                                                                Execution Details
                                                            </h6>

                                                            <div className="space-y-4">
                                                                {/* Start & End Times */}
                                                                <div className="relative pl-6 border-l-2 border-gray-200 space-y-4 ml-2">
                                                                    {customer.visitStart && (
                                                                        <div className="relative">
                                                                            <div className="absolute -left-[31px] top-1 bg-white p-0.5 rounded-full border border-gray-300">
                                                                                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                                                            </div>
                                                                            <div className="flex items-start">
                                                                                <div className="flex-1">
                                                                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-0.5 flex items-center">
                                                                                        <Clock className="h-3 w-3 mr-1" />
                                                                                        Meeting Started
                                                                                    </p>
                                                                                    <p className="text-sm font-medium text-gray-900">{formatDate(customer.visitStart.timestamp, { includeTime: true })}</p>
                                                                                    {customer.visitStart.fullAddress && (
                                                                                        <p className="text-xs text-gray-500 mt-1 flex items-start">
                                                                                            <MapPin className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                                                                                            <span className="flex-1">{customer.visitStart.fullAddress}</span>
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {customer.visitEnd && (
                                                                        <div className="relative pt-2">
                                                                            <div className="absolute -left-[31px] top-3 bg-white p-0.5 rounded-full border border-gray-300">
                                                                                <div className="w-2 h-2 bg-green-500 rounded-full" />
                                                                            </div>
                                                                            <div className="flex items-start">
                                                                                <div className="flex-1">
                                                                                    <p className="text-xs font-semibold text-gray-500 uppercase mb-0.5 flex items-center">
                                                                                        <Clock className="h-3 w-3 mr-1" />
                                                                                        Meeting Completed
                                                                                    </p>
                                                                                    <p className="text-sm font-medium text-gray-900">{formatDate(customer.visitEnd.timestamp, { includeTime: true })}</p>
                                                                                    {customer.visitEnd.fullAddress && (
                                                                                        <p className="text-xs text-gray-500 mt-1 flex items-start">
                                                                                            <MapPin className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                                                                                            <span className="flex-1">{customer.visitEnd.fullAddress}</span>
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Duration Highlight */}
                                                                    {customer.visitStart && customer.visitEnd && (
                                                                        <div className="relative pt-2">
                                                                            <div className="absolute -left-[31px] top-3 bg-white p-0.5 rounded-full border border-gray-300">
                                                                                <div className="w-2 h-2 bg-[#0F2942] rounded-full" />
                                                                            </div>
                                                                            <div className="bg-blue-50/50 rounded-lg p-3 mr-4 border border-blue-100 shadow-sm inline-block">
                                                                                <p className="text-[10px] font-bold text-[#0F2942] uppercase flex items-center mb-1">
                                                                                    <Clock className="h-3 w-3 mr-1" />
                                                                                    Visit Duration
                                                                                </p>
                                                                                <p className="text-sm font-bold text-blue-800">
                                                                                    {calculateVisitDuration(customer.visitStart.timestamp, customer.visitEnd.timestamp)}
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Outcome */}
                                                                {customer.visitOutcome && (
                                                                    <div className="bg-white p-3 rounded border border-gray-200 shadow-sm">
                                                                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Outcome</label>
                                                                        <p className="text-sm font-medium text-gray-900 break-all word-break">{customer.visitOutcome}</p>
                                                                    </div>
                                                                )}

                                                                {/* Notes */}
                                                                {customer.visitNotes && (
                                                                    <div className="bg-white p-3 rounded border border-gray-200 shadow-sm">
                                                                        <label className="text-xs font-semibold text-gray-500 uppercase flex items-center mb-1">
                                                                            <MessageSquare className="h-3 w-3 mr-1" />
                                                                            Field Notes
                                                                        </label>
                                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap break-all word-break">{customer.visitNotes}</p>
                                                                    </div>
                                                                )}

                                                                {/* Images */}
                                                                {customer.visitImages && customer.visitImages.length > 0 && (
                                                                    <div className="pt-2">
                                                                        <label className="text-xs font-semibold text-gray-500 uppercase flex items-center mb-2">
                                                                            <ImageIcon className="h-3 w-3 mr-1" />
                                                                            Visit Photos ({customer.visitImages.length})
                                                                        </label>
                                                                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                                                                            {customer.visitImages.map((img, imgIdx) => (
                                                                                <a
                                                                                    key={img.publicId || imgIdx}
                                                                                    href={img.secureUrl || img.url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="relative block aspect-square rounded-md overflow-hidden bg-gray-200 border border-gray-300 hover:opacity-90 hover:shadow-md transition-all group"
                                                                                >
                                                                                    <img
                                                                                        src={img.secureUrl || img.url}
                                                                                        alt={`Visit photo ${imgIdx + 1}`}
                                                                                        className="absolute inset-0 w-full h-full object-cover"
                                                                                        loading="lazy"
                                                                                    />
                                                                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                                                                                        <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 drop-shadow-md" />
                                                                                    </div>
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-12 border border-gray-200 rounded-lg bg-gray-50 text-gray-500">
                                <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                <p className="capitalize">No customers assigned to this route plan.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Notes */}
                {routePlan.notes && (
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-[#0F2942]" />
                            Additional Notes
                        </h4>
                        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                            <p className="text-gray-800 whitespace-pre-wrap break-words">{routePlan.notes}</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoutePlanDetails;
